import React, { useState } from 'react';
import { useApolloClient } from '@apollo/client';
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import { useController, useForm } from 'react-hook-form';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import {
  BLOCK_USER_MUTATION,
  REPORT_AND_BLOCK_CONTENT_MUTATION,
} from '../../gql';
import {
  Button,
  SurfaceTextField,
  Switch,
  SwitchLabel,
} from '../design-system';

export interface ContentSafetyTarget {
  entityId: string;
  entityType: 'FIND' | 'SITE' | 'FIND_GROUP' | 'OBJECT_GROUP';
  ownerUserId: string;
  report: boolean;
}

interface ContentSafetyDialogProps {
  target: ContentSafetyTarget;
  onClose: () => void;
}

const reasons = defineMessages({
  ABUSE: { id: 'contentSafety.reason.abuse', defaultMessage: 'Abuse' },
  ILLEGAL_CONTENT: {
    id: 'contentSafety.reason.illegal',
    defaultMessage: 'Illegal content',
  },
  PRIVACY_OR_SENSITIVE_LOCATION: {
    id: 'contentSafety.reason.privacy',
    defaultMessage: 'Privacy or sensitive location',
  },
  RIGHTS_INFRINGEMENT: {
    id: 'contentSafety.reason.rights',
    defaultMessage: 'Rights infringement',
  },
  SPAM_OR_DECEPTION: {
    id: 'contentSafety.reason.spam',
    defaultMessage: 'Spam or deception',
  },
  OTHER: { id: 'contentSafety.reason.other', defaultMessage: 'Other' },
});

interface ReportForm {
  mode: 'block' | 'reportAndBlock' | 'none';
  reason: keyof typeof reasons;
  details: string;
}

const affectedFields = [
  'adminContentReports',
  'getBlockedUsers',
  'getFindGroup',
  'getFindGroupFinds',
  'getMyContact',
  'getMyContacts',
  'getMyFieldSession',
  'getMyFieldSessions',
  'getMyFind',
  'getMyFindGroups',
  'getMyFinds',
  'getMyMapFinds',
  'getMyObjectGroups',
  'getMyObjects',
  'getMySite',
  'getMySites',
  'getObjectGroup',
  'getSiteCollaborators',
  'getSiteFinds',
  'smartSearch',
];
const detailsInputProps = { maxLength: 1000 };
const sectionStyles = {
  border: '1px solid',
  borderColor: 'softBorder',
  borderRadius: 2,
  p: 2,
};
const loadingStyles = {
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 220,
};

export const ContentSafetyDialog = React.memo(function ContentSafetyDialog({
  target,
  onClose,
}: ContentSafetyDialogProps) {
  const intl = useIntl();
  const client = useApolloClient();
  const [phase, setPhase] = useState<'editing' | 'refreshing' | 'complete'>(
    'editing'
  );

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { isSubmitting, errors },
  } = useForm<ReportForm>({
    defaultValues: {
      mode: target.report ? 'reportAndBlock' : 'block',
      reason: 'ABUSE',
      details: '',
    },
  });

  const {
    field: { ref: reasonRef, ...reasonField },
  } = useController({ control, name: 'reason' });
  const {
    field: { ref: detailsRef, ...detailsField },
  } = useController({ control, name: 'details' });

  const mode = watch('mode');
  const reporting = mode === 'reportAndBlock';
  const blocking = mode !== 'none';
  const complete = phase === 'complete';
  const showForm = phase === 'editing' && !isSubmitting;

  const handleReportToggle = (
    _event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    setValue('mode', checked ? 'reportAndBlock' : 'block');
  };

  const handleBlockToggle = (
    _event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    setValue('mode', checked ? 'block' : 'none');
  };

  const handleDialogClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  const handleConfirm = async (values: ReportForm) => {
    if (values.mode === 'none' || complete) {
      return;
    }

    clearErrors();

    try {
      if (phase === 'editing') {
        if (values.mode === 'reportAndBlock') {
          await client.mutate({
            mutation: REPORT_AND_BLOCK_CONTENT_MUTATION,
            variables: {
              input: {
                entityId: target.entityId,
                entityType: target.entityType,
                reason: values.reason,
                details: values.details.trim() || null,
              },
            },
          });
        } else {
          await client.mutate({
            mutation: BLOCK_USER_MUTATION,
            variables: { userId: target.ownerUserId },
          });
        }

        setPhase('refreshing');
      }

      const refetches = client.refetchQueries({
        updateCache(cache) {
          for (const fieldName of affectedFields) {
            cache.evict({ id: 'ROOT_QUERY', fieldName });
          }
        },
      });
      const results = await Promise.allSettled(refetches.results);
      const failure = results.find((result) => result.status === 'rejected');

      if (failure?.status === 'rejected') {
        throw failure.reason;
      }

      setPhase('complete');
    } catch (error) {
      setError('root', {
        message:
          error instanceof Error
            ? error.message
            : intl.formatMessage({
                id: 'contentSafety.error',
                defaultMessage: 'The action could not be completed.',
              }),
      });
    }
  };

  const handleFormSubmit = handleSubmit(handleConfirm);
  const reportSwitch = (
    <Switch checked={reporting} onChange={handleReportToggle} />
  );
  const blockSwitch = (
    <Switch
      checked={blocking}
      disabled={reporting}
      onChange={handleBlockToggle}
    />
  );

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      onClose={handleDialogClose}
      open
      aria-labelledby="content-safety-title"
    >
      <Box component="form" onSubmit={handleFormSubmit}>
        <DialogTitle id="content-safety-title">
          <FormattedMessage
            id="contentSafety.dialog.title"
            defaultMessage="Report or block"
          />
        </DialogTitle>
        <DialogContent dividers>
          <Stack gap={2}>
            {errors.root && (
              <Alert severity="error">{errors.root.message}</Alert>
            )}
            {isSubmitting && (
              <Stack gap={2} role="status" sx={loadingStyles}>
                <CircularProgress size={32} />
                <Typography color="text.secondary" variant="body2">
                  <FormattedMessage
                    id="contentSafety.processing"
                    defaultMessage="Applying changes and refreshing content…"
                  />
                </Typography>
              </Stack>
            )}
            {complete && (
              <Alert severity="success">
                <FormattedMessage
                  id="contentSafety.success"
                  defaultMessage="The action was completed successfully."
                />
              </Alert>
            )}
            {showForm && (
              <>
                <SwitchLabel
                  control={reportSwitch}
                  label={intl.formatMessage({
                    id: 'contentSafety.report.toggle',
                    defaultMessage: 'Include content report',
                  })}
                />
                {reporting && (
                  <Stack gap={2} sx={sectionStyles}>
                    <SurfaceTextField
                      {...reasonField}
                      inputRef={reasonRef}
                      label={intl.formatMessage({
                        id: 'contentSafety.report.reason',
                        defaultMessage: 'Reason',
                      })}
                      select
                      surface="sunken"
                    >
                      {Object.entries(reasons).map(([value, message]) => (
                        <MenuItem key={value} value={value}>
                          {intl.formatMessage(message)}
                        </MenuItem>
                      ))}
                    </SurfaceTextField>
                    <SurfaceTextField
                      {...detailsField}
                      inputRef={detailsRef}
                      inputProps={detailsInputProps}
                      label={intl.formatMessage({
                        id: 'contentSafety.report.details',
                        defaultMessage: 'Additional details',
                      })}
                      maxRows={6}
                      minRows={3}
                      multiline
                      surface="sunken"
                    />
                  </Stack>
                )}
                <SwitchLabel
                  control={blockSwitch}
                  label={intl.formatMessage({
                    id: 'contentSafety.block.toggle',
                    defaultMessage: 'Block content owner',
                  })}
                />
                {blocking && (
                  <Typography
                    color="text.secondary"
                    sx={sectionStyles}
                    variant="body2"
                  >
                    <FormattedMessage
                      id="contentSafety.block.description"
                      defaultMessage="Blocking removes your contact and sharing relationships with this user."
                    />
                  </Typography>
                )}
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={isSubmitting}
            onClick={handleDialogClose}
            tone="neutral"
            type="button"
          >
            {complete ? (
              <FormattedMessage id="action.close" defaultMessage="Close" />
            ) : (
              <FormattedMessage id="action.cancel" defaultMessage="Cancel" />
            )}
          </Button>
          {!complete && (
            <Button
              disabled={isSubmitting || !blocking}
              tone="danger"
              type="submit"
            >
              {phase === 'refreshing' ? (
                <FormattedMessage
                  id="contentSafety.action.retryRefresh"
                  defaultMessage="Retry refresh"
                />
              ) : (
                <FormattedMessage
                  id="contentSafety.action.confirm"
                  defaultMessage="Confirm"
                />
              )}
            </Button>
          )}
        </DialogActions>
      </Box>
    </Dialog>
  );
});
